import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductPrintingFormRefs
 * @property {HTMLInputElement} noPrintingButton
 * @property {HTMLInputElement} spelerButton
 * @property {HTMLInputElement} customButton
 * @property {HTMLDivElement} spelerFields
 * @property {HTMLDivElement} customFields
 * @property {HTMLSelectElement} playerSelect
 * @property {HTMLInputElement} customNumber
 * @property {HTMLInputElement} customName
 * @property {HTMLDivElement} playerError
 * @property {HTMLSpanElement} playerErrorText
 * @property {HTMLDivElement} customError
 * @property {HTMLSpanElement} customErrorText
 */

/**
 * @extends {Component<ProductPrintingFormRefs>}
 */
class ProductPrintingForm extends Component {
  static Mode = {
    NO: 'no',
    SPELER: 'speler',
    CUSTOM: 'custom',
  };

  requiredRefs = [
    'noPrintingButton',
    'spelerButton',
    'customButton',
    'spelerFields',
    'customFields',
    'playerSelect',
    'customNumber',
    'customName',
    'playerError',
    'customError',
  ];

  /** @type {string} */
  #currentMode = ProductPrintingForm.Mode.NO;

  connectedCallback() {
    super.connectedCallback();
    this.#initializeForm();
    // The event-delegation layer doesn't handle composition events, so bind it
    // directly. Dead-key accents (e.g. ⌥+u then e → ë) finish on compositionend.
    this.refs.customName.addEventListener('compositionend', this.#onCustomNameCompositionEnd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.customName?.removeEventListener('compositionend', this.#onCustomNameCompositionEnd);
  }

  #initializeForm() {
    this.#currentMode = ProductPrintingForm.Mode.NO;
    this.#updateButtonStates(ProductPrintingForm.Mode.NO);
    this.refs.spelerFields.hidden = true;
    this.refs.customFields.hidden = true;
    this.#resetSpelerFields();
    this.#resetCustomFields();
    this.clearError();
  }

  /**
   * @returns {boolean}
   */
  isPrintingSelected() {
    return this.refs.spelerButton.checked || this.refs.customButton.checked;
  }

  /**
   * @returns {{ valid: boolean, player?: string, number?: string, error?: string }}
   */
  validate() {
    if (this.refs.spelerButton.checked) {
      return this.#validateSpeler();
    }

    if (this.refs.customButton.checked) {
      return this.#validateCustom();
    }

    return { valid: true };
  }

  /**
   * @returns {{ valid: boolean, player?: string, number?: string, error?: string }}
   */
  #validateSpeler() {
    const selectedOption = this.refs.playerSelect.selectedOptions[0];
    const player = this.refs.playerSelect.value.trim();

    if (!player) {
      const error = this.dataset.playerRequiredError || 'Please select a player';
      return { valid: false, error };
    }

    const number = selectedOption?.dataset.number?.trim() || '';

    return { valid: true, player, number };
  }

  /**
   * @returns {{ valid: boolean, player?: string, number?: string, error?: string }}
   */
  #validateCustom() {
    const number = this.refs.customNumber.value.trim();
    const name = this.refs.customName.value.trim();

    if (!/^[0-9]{1,2}$/.test(number) || Number(number) < 1 || Number(number) > 99) {
      const error = this.dataset.customNumberError || 'Please enter a number between 1 and 99';
      return { valid: false, error };
    }

    if (!/^[\p{L}\p{M} .'’-]{1,20}$/u.test(name) || !/\p{L}/u.test(name)) {
      const error = this.dataset.customNameError || 'Please enter a name (letters only, max 20)';
      return { valid: false, error };
    }

    return { valid: true, player: name, number };
  }

  clearError() {
    this.refs.playerError.classList.add('hidden');
    if (this.refs.playerErrorText) {
      this.refs.playerErrorText.textContent = '';
    }
    this.refs.playerSelect.removeAttribute('aria-invalid');
    this.refs.playerSelect.removeAttribute('aria-describedby');

    this.refs.customError.classList.add('hidden');
    if (this.refs.customErrorText) {
      this.refs.customErrorText.textContent = '';
    }
    this.refs.customNumber.removeAttribute('aria-invalid');
    this.refs.customName.removeAttribute('aria-invalid');
  }

  /**
   * @param {string} message
   */
  showError(message) {
    if (this.refs.customButton.checked) {
      this.#showCustomError(message);
      return;
    }

    this.refs.playerError.classList.remove('hidden');
    if (this.refs.playerErrorText) {
      this.refs.playerErrorText.textContent = message;
    }
    this.refs.playerSelect.setAttribute('aria-invalid', 'true');
    if (this.refs.playerError.id) {
      this.refs.playerSelect.setAttribute('aria-describedby', this.refs.playerError.id);
    }
    this.refs.playerSelect.focus();
  }

  /**
   * @param {string} message
   */
  #showCustomError(message) {
    this.refs.customError.classList.remove('hidden');
    if (this.refs.customErrorText) {
      this.refs.customErrorText.textContent = message;
    }

    const number = this.refs.customNumber.value.trim();
    const numberInvalid = !/^[0-9]{1,2}$/.test(number) || Number(number) < 1 || Number(number) > 99;
    const target = numberInvalid ? this.refs.customNumber : this.refs.customName;

    target.setAttribute('aria-invalid', 'true');
    if (this.refs.customError.id) {
      target.setAttribute('aria-describedby', this.refs.customError.id);
    }
    target.focus();
  }

  /**
   * @param {string} mode
   * @param {Event} _event
   */
  togglePrinting(mode, _event) {
    if (!Object.values(ProductPrintingForm.Mode).includes(mode)) return;
    if (this.#currentMode === mode) return;

    this.#currentMode = mode;
    this.#updateButtonStates(mode);
    this.clearError();

    const isSpeler = mode === ProductPrintingForm.Mode.SPELER;
    const isCustom = mode === ProductPrintingForm.Mode.CUSTOM;

    this.refs.spelerFields.hidden = !isSpeler;
    this.refs.customFields.hidden = !isCustom;

    if (isSpeler) {
      this.#resetCustomFields();
      this.refs.playerSelect.focus();
    } else if (isCustom) {
      this.#resetSpelerFields();
      this.refs.customNumber.focus();
    } else {
      this.#resetSpelerFields();
      this.#resetCustomFields();
    }
  }

  /**
   * @param {string} mode
   */
  #updateButtonStates(mode) {
    this.refs.noPrintingButton.checked = mode === ProductPrintingForm.Mode.NO;
    this.refs.spelerButton.checked = mode === ProductPrintingForm.Mode.SPELER;
    this.refs.customButton.checked = mode === ProductPrintingForm.Mode.CUSTOM;
  }

  /**
   * @param {Event} _event
   */
  onPlayerChange(_event) {
    this.clearError();
  }

  /**
   * Strips non-digit characters and clamps to 1-99 (text input, digits only).
   * @param {Event} _event
   */
  onCustomNumberInput(_event) {
    const input = this.refs.customNumber;
    input.value = input.value.replace(/[^0-9]/g, '').slice(0, 2);
    this.clearError();
  }

  /**
   * @param {InputEvent} event
   */
  onCustomNameInput(event) {
    // Don't mutate the field mid-composition: rewriting the value cancels the
    // IME/dead-key sequence (e.g. ⌥+u then e → ë). Wait for compositionend.
    if (event.isComposing) return;
    this.#sanitizeCustomName();
    this.clearError();
  }

  /** @type {() => void} */
  #onCustomNameCompositionEnd = () => {
    this.#sanitizeCustomName();
    this.clearError();
  };

  /**
   * Strips disallowed characters and limits to 20 characters. Allows letters
   * (incl. accented, e.g. ë é), combining marks, spaces, hyphens, apostrophes
   * (straight and typographic) and periods — the characters used in names.
   */
  #sanitizeCustomName() {
    const input = this.refs.customName;
    input.value = input.value.replace(/[^\p{L}\p{M} .'’-]/gu, '').slice(0, 20);
  }

  #resetSpelerFields() {
    this.refs.playerSelect.value = '';
  }

  #resetCustomFields() {
    this.refs.customNumber.value = '';
    this.refs.customName.value = '';
  }

  /**
   * @returns {string | undefined}
   */
  get printingVariantId() {
    return this.dataset.printingVariantId;
  }
}

if (!customElements.get('product-printing-form')) {
  customElements.define('product-printing-form', ProductPrintingForm);
}

export { ProductPrintingForm };
